import { ArrowLeft, ExternalLink, Info } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import type { SharedRecipePreview } from '../../shared/api';
import { safeHttpUrl } from '../../shared/recipes';
import { SHARED_RECIPE_PATH, sharedByLine } from '../../shared/sharing';
import { ApiError } from '../api/client';
import { useAddSharedRecipe, useSettings, useSharedRecipe } from '../api/hooks';
import { FatSecretAttribution } from '../components/FatSecretAttribution';
import { NutritionList } from '../components/NutritionPanel';
import { grams as formatGrams, pluralize } from '../lib/format';
import { forgetSharedToken, sharedToken, takeSharedLink } from '../lib/openedShareLink';
import { useUI } from '../lib/ui';

/** Add to my recipes, and a way out beside it: large on its own, small inside a banner. */
function AddButtons(props: { pending: boolean; hintId: string; onAdd: () => void; onLeave: () => void; secondary: string; inBanner?: boolean }) {
  const { pending, hintId, onAdd, onLeave, secondary, inBanner = false } = props;
  return (
    <div className="row wrap">
      {/* aria-disabled while adding keeps keyboard focus here; a second press is ignored. */}
      <button className={`btn btn--primary ${inBanner ? 'btn--sm' : 'btn--lg'}`} aria-disabled={pending || undefined} aria-describedby={hintId} onClick={onAdd}>
        Add to my recipes
      </button>
      <button className={inBanner ? 'btn btn--ghost btn--sm' : 'btn btn--quiet'} onClick={onLeave}>
        {secondary}
      </button>
    </div>
  );
}

/** What's on offer: your own recipe, a copy you have (from this link or an older one), your own version of it, or a new copy. */
function Offer({
  preview,
  pending,
  onAdd,
  onLeave,
}: {
  preview: SharedRecipePreview;
  pending: boolean;
  onAdd: () => void;
  onLeave: (to: string) => void;
}) {
  const hintId = useId();
  if (preview.yours) {
    return (
      <div className="banner banner--ai">
        <Info size={17} aria-hidden="true" />
        <div className="stack grow" style={{ gap: 10 }}>
          <span className="small">This is your own recipe. People who open your link see it like this.</span>
          <button className="btn btn--ghost btn--sm" style={{ alignSelf: 'flex-start' }} onClick={() => onLeave('/recipes')}>
            Go to your recipes
          </button>
        </div>
      </div>
    );
  }
  const copy = preview.copy;
  if (copy?.edited) {
    // Theirs since they edited it: it stays as it is, and a fresh copy can sit beside it.
    return (
      <div className="banner banner--ai">
        <Info size={17} aria-hidden="true" />
        <div className="stack grow" style={{ gap: 10 }}>
          <span id={hintId} className="small">
            You have your own version of this recipe. Adding gives you a fresh copy beside it.
          </span>
          <AddButtons pending={pending} hintId={hintId} onAdd={onAdd} onLeave={() => onLeave(`/recipes/${copy.id}`)} secondary="Open my version" inBanner />
        </div>
      </div>
    );
  }
  if (copy) {
    return (
      <div className="banner banner--ai">
        <Info size={17} aria-hidden="true" />
        <div className="stack grow" style={{ gap: 10 }}>
          <span id={hintId} className="small">
            {copy.fromOlderLink
              ? 'You have a copy from an older link. Updating replaces it with this version. What you logged stays as it is.'
              : 'You already have this recipe.'}
          </span>
          <div className="row wrap">
            {copy.fromOlderLink && (
              <button className="btn btn--primary btn--sm" aria-disabled={pending || undefined} aria-describedby={hintId} onClick={onAdd}>
                Update my copy
              </button>
            )}
            <button className={`btn btn--sm ${copy.fromOlderLink ? 'btn--ghost' : 'btn--primary'}`} onClick={() => onLeave(`/recipes/${copy.id}`)}>
              Open my copy
            </button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="stack" style={{ gap: 8 }}>
      <AddButtons pending={pending} hintId={hintId} onAdd={onAdd} onLeave={() => onLeave('/recipes')} secondary="Not now" />
      <p id={hintId} className="tiny muted">
        You get your own copy to log and change.
      </p>
    </div>
  );
}

/**
 * A recipe someone shared by link: a preview (the recipe and who shared it, nothing else of
 * theirs), then Add to my recipes or Update my copy. The link's token was moved out of the address
 * bar when the app loaded (lib/openedShareLink.ts), so it outlives signing in; it's forgotten once
 * the recipe is added, the preview dismissed, or the link found to be off.
 */
export function SharedRecipePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useUI();
  const [token, setToken] = useState(() => sharedToken());
  const preview = useSharedRecipe(token);
  const add = useAddSharedRecipe();
  const settings = useSettings();
  const nutritionId = useId();
  const ingredientsId = useId();
  const notesId = useId();
  const defs = useMemo(() => new Map((settings.data?.nutrients ?? []).map((n) => [n.key, n])), [settings.data]);
  const tracked = settings.data?.tracking.filter((t) => t.tracked).map((t) => t.key) ?? ['kcal', 'protein', 'carbs', 'fat'];

  // A new member arrives straight from setup, a long page at the same address: start at the top, with the recipe.
  // Block body on purpose: scrollTo can return a Promise, and an effect must return nothing or a cleanup.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // A link opened while this page is already showing (only the address's fragment changed): it takes over,
  // and what happened to the last one's Add no longer applies.
  const resetAdd = add.reset;
  useEffect(() => {
    if (!location.hash.startsWith('#r=')) return;
    takeSharedLink(location.hash);
    setToken(sharedToken());
    resetAdd();
    navigate(SHARED_RECIPE_PATH, { replace: true });
  }, [location.hash, navigate, resetAdd]);

  // Off, replaced or never a link: say so, even over a preview that was showing (React Query keeps the
  // last answer when a later one fails), as when Add finds the link turned off. A dead link never
  // comes back, so it's forgotten.
  const gone = [preview.error, add.error].some((e) => e instanceof ApiError && e.status === 404);
  useEffect(() => {
    if (gone) forgetSharedToken();
  }, [gone]);

  /** Done with this link: it's forgotten, and the preview gives way to `to`. */
  const leave = (to: string) => {
    forgetSharedToken();
    navigate(to, { replace: true });
  };

  const addIt = () => {
    if (!token || add.isPending) return;
    add.mutate(token, {
      onSuccess: ({ recipe, updated }) => {
        forgetSharedToken();
        toast(updated ? `Updated your copy of ${recipe.name}` : `${recipe.name} is in your recipes`);
        // Replacing the preview in history, so Back doesn't offer to add it again.
        navigate(`/recipes/${recipe.id}`, { replace: true, state: { focusHeading: true } });
      },
      // A link turned off meanwhile shows as such below; anything else gets a toast.
      onError: (e) => {
        if (!(e instanceof ApiError && e.status === 404)) toast(e.message, { tone: 'error' });
      },
    });
  };

  const back = (
    <Link to="/recipes" className="btn btn--quiet btn--sm" style={{ alignSelf: 'flex-start', marginBottom: -8 }} onClick={() => forgetSharedToken()}>
      <ArrowLeft size={16} aria-hidden="true" /> Recipes
    </Link>
  );

  if (!token) {
    return (
      <div className="page">
        {back}
        <section className="card card--pad stack">
          <h1 className="section-title">No shared recipe here</h1>
          <p className="small muted">To see a recipe someone shared with you, open the link they sent.</p>
        </section>
      </div>
    );
  }

  const data = preview.data;
  if (gone || (preview.isError && !data)) {
    return (
      <div className="page">
        {back}
        <section className="card card--pad stack" role="alert">
          <h1 className="section-title">{gone ? "This link doesn't work anymore" : "Couldn't open this recipe"}</h1>
          <p className="small muted">{gone ? 'It may have been turned off or replaced. Ask whoever sent it for a new one.' : preview.error?.message}</p>
          {!gone && (
            <button className="btn btn--ghost btn--sm" style={{ alignSelf: 'flex-start' }} onClick={() => void preview.refetch()}>
              Try again
            </button>
          )}
        </section>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="page" aria-busy="true">
        <div className="skeleton" style={{ height: 34, width: 120 }} />
        <div className="skeleton" style={{ height: 48 }} />
        <div className="skeleton" style={{ height: 220, borderRadius: 20 }} />
      </div>
    );
  }

  const link = data.sourceUrl ? safeHttpUrl(data.sourceUrl) : null;
  return (
    <div className="page">
      {back}
      <div className="page__head">
        <div className="grow">
          <p className="eyebrow">Shared recipe</p>
          <h1 className="page__title recipe-title">{data.name}</h1>
          <p className="page__sub">
            {sharedByLine(data.sharedBy)} · <span className="num">{pluralize(data.servings, 'serving')}</span>
          </p>
        </div>
      </div>

      <Offer preview={data} pending={add.isPending} onAdd={addIt} onLeave={leave} />

      <section className="card card--pad" aria-labelledby={nutritionId}>
        <h2 id={nutritionId} className="card__title">
          Nutrition per serving
        </h2>
        <p className="small muted" style={{ margin: '4px 0 6px' }}>
          One serving is {formatGrams(data.servingGrams)}
          {data.cooked ? ' of the cooked dish' : ''}.
        </p>
        <NutritionList amounts={data.perServing} defs={defs} tracked={tracked} coverage={{ known: data.known, count: data.ingredientCount }} />
      </section>

      <section className="card card--pad" aria-labelledby={ingredientsId}>
        <h2 id={ingredientsId} className="card__title">
          Ingredients
        </h2>
        <ul className="recipe-ingredients">
          {data.ingredients.map((i, at) => (
            <li key={at}>
              <span className="grow recipe-ingredients__what">
                {i.amount && (
                  <>
                    <span className="num recipe-ingredients__unit">{i.amount}</span>{' '}
                  </>
                )}
                <span className="recipe-ingredients__name">{i.name}</span>
                {i.brand && <span className="tiny muted"> · {i.brand}</span>}
              </span>
              <span className="num small recipe-ingredients__grams">{formatGrams(i.grams, 1)}</span>
            </li>
          ))}
        </ul>
        {data.fatsecret && <FatSecretAttribution />}
      </section>

      {data.notes && (
        <section className="card card--pad" aria-labelledby={notesId}>
          <h2 id={notesId} className="card__title">
            Notes
          </h2>
          <p className="recipe-notes small">{data.notes}</p>
        </section>
      )}

      {link && (
        <a className="btn btn--ghost btn--sm btn--wrap" style={{ alignSelf: 'flex-start' }} href={link} target="_blank" rel="noopener noreferrer nofollow">
          <ExternalLink size={15} aria-hidden="true" /> Original recipe
          <span className="muted">· {new URL(link).hostname}</span>
          <span className="sr-only"> (opens in a new tab)</span>
        </a>
      )}
    </div>
  );
}
